// Colour arithmetic, not styling: this file is exempt from DS-TOKEN-01 in eslint.config.js
// because it has to parse and rebuild `rgb()` strings to measure perceived contrast.
import { darken, getContrastRatio, lighten } from '@mui/material/styles';
import { CONTRAST } from '../tokens/color.js';
/**
 * Darken (light mode) or lighten (dark mode) a colour until it meets a WCAG contrast
 * ratio against the given background. A tenant supplies an arbitrary brand colour; this
 * keeps it legible as text/links even when the raw hue is pale. Filled surfaces (buttons,
 * active nav) are handled separately by MUI's `contrastThreshold`.
 */
export function accessibleText(color, bg, lightMode, min = CONTRAST.text) {
    let c = color;
    for (let i = 0; i < 24 && getContrastRatio(c, bg) < min; i += 1) {
        c = lightMode ? darken(c, 0.06) : lighten(c, 0.06);
    }
    return c;
}
/**
 * Perceived contrast: composite any translucency onto the backdrop, then measure. MUI's
 * `getContrastRatio` ignores the alpha channel, so an `rgba(0,0,0,0.6)` scores as pure
 * black unless it is flattened first.
 */
export function perceivedContrast(foreground, backdrop) {
    return getContrastRatio(composite(foreground, backdrop), backdrop);
}
const RGBA = /rgba?\(\s*(\d+)[,\s]+(\d+)[,\s]+(\d+)(?:[,/\s]+([\d.]+))?\s*\)/i;
const RGB = /rgba?\(\s*(\d+)[,\s]+(\d+)[,\s]+(\d+)/i;
function composite(color, backdrop) {
    const rgba = RGBA.exec(color);
    if (!rgba)
        return color;
    const alpha = rgba[4] === undefined ? 1 : Number(rgba[4]);
    if (alpha >= 1)
        return `rgb(${rgba[1]}, ${rgba[2]}, ${rgba[3]})`;
    const bg = toRgb(backdrop);
    const mix = [1, 2, 3].map((i) => Math.round(alpha * Number(rgba[i]) + (1 - alpha) * (bg[i - 1] ?? 0)));
    return `rgb(${mix[0]}, ${mix[1]}, ${mix[2]})`;
}
function toRgb(color) {
    const rgb = RGB.exec(color);
    if (rgb)
        return [Number(rgb[1]), Number(rgb[2]), Number(rgb[3])];
    const clean = color.replace('#', '');
    const full = clean.length === 3
        ? clean
            .split('')
            .map((c) => c + c)
            .join('')
        : clean;
    return [
        parseInt(full.slice(0, 2), 16),
        parseInt(full.slice(2, 4), 16),
        parseInt(full.slice(4, 6), 16),
    ];
}
//# sourceMappingURL=contrast.js.map